import type { ReactNode } from 'react';

import { Popover as PopoverPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/shared/ui/cn';

export type PopoverProps = {
  trigger: ReactNode;
  /**
   * Le nom de la bulle pour les lecteurs d'écran (elle s'annonce comme une
   * fenêtre) : « Choisir une date », « Réagir », « Filtres ».
   */
  label: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
};

/** Bulle accrochée à un bouton : un calendrier, une liste de choix, un aperçu. */
export function Popover({
  trigger,
  label,
  children,
  open,
  onOpenChange,
  align = 'start',
  side = 'bottom',
  className,
}: PopoverProps) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          aria-label={label}
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 animate-scale-in rounded-md border border-line bg-raised shadow-popover outline-none',
            className,
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export type TooltipProps = {
  label: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
};

/**
 * Info-bulle d'ordinateur : le nom d'une icône de navigation, un raccourci
 * clavier. Jamais une information qu'on ne trouverait pas autrement : un
 * téléphone n'a pas de survol.
 */
export function Tooltip({ label, children, side = 'right' }: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={350}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          className="z-50 animate-fade-in rounded-sm bg-fg px-2.5 py-1.5 text-caption text-bg shadow-floating"
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
